import React, { useState, useEffect } from "react";
import { X } from "lucide-react";
import InputMask from "react-input-mask";

const CustomerForm = ({ onClose, onSave }) => {
  const [activeTab, setActiveTab] = useState("geral");
  const [error, setError] = useState("");
  const [municipioError, setMunicipioError] = useState("");
  const [loadingMunicipios, setLoadingMunicipios] = useState(false);
  const [municipios, setMunicipios] = useState([]);

  const [formData, setFormData] = useState({
    RAZAO_SOCIAL: "",
    NOME_FANTASIA: "",
    CNPJ: "",
    FISICA_JURIDICA: "J",
    TIPO: "R",
    TELEFONE_GERAL_1: "",
    TELEFONE_GERAL_2: "",
    EMAIL_NFE: "",
    ENDERECO_CEP: "",
    ENDERECO_RUA: "",
    ENDERECO_NUMERO: "",
    ENDERECO_BAIRRO: "",
    ENDERECO_COMPLEMENTO: "",
    MUNICIPIO_CODIGO_IBGE: "",
    ESTADO_CODIGO_IBGE: "",
    INSCRICAO_ESTADUAL: "",
    INSCRICAO_MUNICIPAL: "",
    INSCRICAO_RURAL: "",
    SUFRAMA: "",
    REGIME_TRIBUTARIO: false,
    STATUS: "4",
    fornecedores: [
      { nome: "", telefone: "" },
      { nome: "", telefone: "" },
      { nome: "", telefone: "" },
    ],
  });

  const [cepLoading, setCepLoading] = useState(false);
  const [cepError, setCepError] = useState("");

  // Novo estado para erros de documento (CPF/CNPJ)
  const [documentError, setDocumentError] = useState("");

  // Estados para controle de loading e mensagens da API
  const [formLoading, setFormLoading] = useState(false);
  const [apiMsg, setApiMsg] = useState("");
  const [serverError, setServerError] = useState(null);

  // Lista de Estados com Siglas e Códigos IBGE
  const estados = [
    { nome: "Acre", sigla: "AC", codigo_ibge: "12" },
    { nome: "Alagoas", sigla: "AL", codigo_ibge: "27" },
    { nome: "Amapá", sigla: "AP", codigo_ibge: "16" },
    { nome: "Amazonas", sigla: "AM", codigo_ibge: "13" },
    { nome: "Bahia", sigla: "BA", codigo_ibge: "29" },
    { nome: "Ceará", sigla: "CE", codigo_ibge: "23" },
    { nome: "Distrito Federal", sigla: "DF", codigo_ibge: "53" },
    { nome: "Espírito Santo", sigla: "ES", codigo_ibge: "32" },
    { nome: "Goiás", sigla: "GO", codigo_ibge: "52" },
    { nome: "Maranhão", sigla: "MA", codigo_ibge: "21" },
    { nome: "Mato Grosso", sigla: "MT", codigo_ibge: "51" },
    { nome: "Mato Grosso do Sul", sigla: "MS", codigo_ibge: "50" },
    { nome: "Minas Gerais", sigla: "MG", codigo_ibge: "31" },
    { nome: "Pará", sigla: "PA", codigo_ibge: "15" },
    { nome: "Paraíba", sigla: "PB", codigo_ibge: "25" },
    { nome: "Paraná", sigla: "PR", codigo_ibge: "41" },
    { nome: "Pernambuco", sigla: "PE", codigo_ibge: "26" },
    { nome: "Piauí", sigla: "PI", codigo_ibge: "22" },
    { nome: "Rio de Janeiro", sigla: "RJ", codigo_ibge: "33" },
    { nome: "Rio Grande do Norte", sigla: "RN", codigo_ibge: "24" },
    { nome: "Rio Grande do Sul", sigla: "RS", codigo_ibge: "43" },
    { nome: "Rondônia", sigla: "RO", codigo_ibge: "11" },
    { nome: "Roraima", sigla: "RR", codigo_ibge: "14" },
    { nome: "Santa Catarina", sigla: "SC", codigo_ibge: "42" },
    { nome: "São Paulo", sigla: "SP", codigo_ibge: "35" },
    { nome: "Sergipe", sigla: "SE", codigo_ibge: "28" },
    { nome: "Tocantins", sigla: "TO", codigo_ibge: "17" },
  ];

  // Função para buscar municípios com base no estado selecionado
  const fetchMunicipios = async (estadoCodigo) => {
    setLoadingMunicipios(true);
    setMunicipioError("");
    setMunicipios([]);
    setFormData((prev) => ({ ...prev, MUNICIPIO_CODIGO_IBGE: "" }));

    try {
      const response = await fetch(
        `https://servicodados.ibge.gov.br/api/v1/localidades/estados/${estadoCodigo}/municipios`
      );

      if (!response.ok) {
        throw new Error("Erro ao buscar municípios.");
      }

      const data = await response.json();
      // Ordenar municípios por nome
      const sortedData = data.sort((a, b) => a.nome.localeCompare(b.nome));
      setMunicipios(sortedData);
      return sortedData; // Retorna a lista de municípios
    } catch (err) {
      setMunicipioError(err.message);
      return []; // Retorna uma lista vazia em caso de erro
    } finally {
      setLoadingMunicipios(false);
    }
  };

  // useEffect para buscar municípios quando o estado for alterado
  useEffect(() => {
    const fetchAndSetMunicipios = async () => {
      if (formData.ESTADO_CODIGO_IBGE) {
        const municipiosList = await fetchMunicipios(
          formData.ESTADO_CODIGO_IBGE
        );
        // Se já tiver um MUNICIPIO_CODIGO_IBGE válido, verificar se está na nova lista
        if (formData.MUNICIPIO_CODIGO_IBGE) {
          const municipioExiste = municipiosList.find(
            (m) => m.id.toString() === formData.MUNICIPIO_CODIGO_IBGE
          );
          if (!municipioExiste) {
            setFormData((prev) => ({ ...prev, MUNICIPIO_CODIGO_IBGE: "" }));
          }
        }
      } else {
        setMunicipios([]);
        setFormData((prev) => ({ ...prev, MUNICIPIO_CODIGO_IBGE: "" }));
      }
    };

    fetchAndSetMunicipios();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.ESTADO_CODIGO_IBGE]);

  // Funções de validação CPF e CNPJ
  const validateCPF = (cpf) => {
    const cleanCPF = cpf.replace(/[^\d]/g, "");

    if (cleanCPF.length !== 11) return false;
    if (/^(\d)\1{10}$/.test(cleanCPF)) return false;

    let sum = 0;
    for (let i = 0; i < 9; i++) {
      sum += parseInt(cleanCPF.charAt(i)) * (10 - i);
    }
    let digit = 11 - (sum % 11);
    const firstDigit = digit >= 10 ? 0 : digit;

    sum = 0;
    for (let i = 0; i < 10; i++) {
      sum += parseInt(cleanCPF.charAt(i)) * (11 - i);
    }
    digit = 11 - (sum % 11);
    const secondDigit = digit >= 10 ? 0 : digit;

    return (
      parseInt(cleanCPF.charAt(9)) === firstDigit &&
      parseInt(cleanCPF.charAt(10)) === secondDigit
    );
  };

  const validateCNPJ = (cnpj) => {
    const cleanCNPJ = cnpj.replace(/[^\d]/g, "");

    if (cleanCNPJ.length !== 14) return false;
    if (/^(\d)\1{13}$/.test(cleanCNPJ)) return false;

    const calcDigit = (arr) => {
      let sum = 0;
      for (let i = 0; i < arr.length; i++) {
        sum += parseInt(cleanCNPJ.charAt(i)) * arr[i];
      }
      const digit = sum % 11;
      return digit < 2 ? 0 : 11 - digit;
    };

    const weight1 = [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
    const weight2 = [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];

    const digit1 = calcDigit(weight1);
    const digit2 = calcDigit(weight2);

    return (
      parseInt(cleanCNPJ.charAt(12)) === digit1 &&
      parseInt(cleanCNPJ.charAt(13)) === digit2
    );
  };

  const handleDocumentValidation = (value) => {
    const cleanValue = value.replace(/[^\d]/g, "");
    const isValid =
      formData.FISICA_JURIDICA === "F"
        ? validateCPF(cleanValue)
        : validateCNPJ(cleanValue);

    if (!isValid && cleanValue.length > 0) {
      setDocumentError(
        formData.FISICA_JURIDICA === "F" ? "CPF inválido" : "CNPJ inválido"
      );
    } else {
      setDocumentError("");
    }
  };

  const handleInputChange = (e, fornecedorIndex = null) => {
    const { name, value, type, checked } = e.target;

    if (name === "CNPJ") {
      handleDocumentValidation(value);
    }

    if (fornecedorIndex !== null) {
      const novosFornecedores = [...formData.fornecedores];
      novosFornecedores[fornecedorIndex] = {
        ...novosFornecedores[fornecedorIndex],
        [name]: type === "checkbox" ? checked : value,
      };
      setFormData({
        ...formData,
        fornecedores: novosFornecedores,
      });
    } else {
      setFormData({
        ...formData,
        [name]: type === "checkbox" ? checked : value,
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.MUNICIPIO_CODIGO_IBGE || !formData.ESTADO_CODIGO_IBGE) {
      setError("Os códigos IBGE de município e estado são obrigatórios.");
      return;
    }

    if (documentError) {
      setError("Por favor, corrija os erros no formulário.");
      return;
    }

    // Limpar mensagens anteriores e iniciar loading
    setError("");
    setApiMsg("");
    setServerError(null);
    setFormLoading(true);

    try {
      const dadosFormatados = {
        ERRO_INTEGRACAO: 0,
        CODIGO: "",
        VENDEDOR_CODIGO: "",
        RAZAO_SOCIAL: formData.RAZAO_SOCIAL.toUpperCase(),
        NOME_FANTASIA: formData.NOME_FANTASIA.toUpperCase(),
        CNPJ: formData.CNPJ.replace(/\D/g, ""),
        RG: "",
        FISICA_JURIDICA: formData.FISICA_JURIDICA,
        INSCRICAO_ESTADUAL: formData.INSCRICAO_ESTADUAL.trim(),
        INSCRICAO_MUNICIPAL: formData.INSCRICAO_MUNICIPAL.trim(),
        INSCRICAO_RURAL: formData.INSCRICAO_RURAL.trim(),
        SUFRAMA: formData.SUFRAMA,
        DATA_FUNDACAO: new Date().toISOString(),
        SITE: "",
        TIPO: formData.TIPO,
        TELEFONE_GERAL_1: formData.TELEFONE_GERAL_1.replace(/\D/g, ""),
        TELEFONE_GERAL_2: formData.TELEFONE_GERAL_2
          ? formData.TELEFONE_GERAL_2.replace(/\D/g, "")
          : "",
        EMAIL_NFE: formData.EMAIL_NFE,
        ENDERECO_RUA: formData.ENDERECO_RUA.trim(),
        ENDERECO_NUMERO: formData.ENDERECO_NUMERO.trim(),
        ENDERECO_BAIRRO: formData.ENDERECO_BAIRRO.trim(),
        ENDERECO_CEP: formData.ENDERECO_CEP.replace(/\D/g, ""),
        ENDERECO_COMPLEMENTO: formData.ENDERECO_COMPLEMENTO
          ? formData.ENDERECO_COMPLEMENTO.trim()
          : "",
        MUNICIPIO_CODIGO_IBGE: parseInt(formData.MUNICIPIO_CODIGO_IBGE),
        ESTADO_CODIGO_IBGE: parseInt(formData.ESTADO_CODIGO_IBGE),
        DATA_CADASTRO: new Date().toISOString(),
        HORA_CADASTRO: new Date().toTimeString().split(" ")[0],
        OBSERVACAO: "",
        STATUS: formData.STATUS,
        REGIME_TRIBUTARIO: formData.REGIME_TRIBUTARIO ? 1 : 0,
        ISENTO_IPI: "0",
        ISENTO_ST: "1",
        ISENTO_ICMS: "0",
        PERCENTUAL_CARGA_MEDIA: 0,
        FUNCAO_TRIBUTARIA_CODIGO: "",
        GRUPO_TRIBUTARIO_CODIGO: "1515",
        fornecedores: formData.fornecedores,
      };

      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/customers`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
          body: JSON.stringify(dadosFormatados),
        }
      );

      if (!response.ok) {
        throw new Error("Erro ao criar cliente");
      }

      const responseData = await response.json();

      if (responseData?.data?.error) {
        // Se houver propriedade error, exibe uma tela de erro
        setServerError(responseData?.data?.error);
      } else if (!responseData?.data?.success) {
        // Se success não for true, exibe a mensagem de erro
        setApiMsg(responseData?.data?.mensagem);
      } else {
        // Se success for true, exibe mensagem e chama onSave
        onSave();
        onClose();
      }
    } catch (error) {
      setError(error.message);
      console.error("Erro ao salvar cliente:", error);
    } finally {
      setFormLoading(false);
    }
  };

  // Função para buscar o endereço pelo CEP
  const fetchAddressByCEP = async (cep) => {
    const cleanCEP = cep.replace(/\D/g, "");

    if (cleanCEP.length !== 8) {
      setCepError("CEP inválido. Deve conter 8 números.");
      return;
    }

    setCepLoading(true);
    setCepError("");

    try {
      const response = await fetch(
        `https://viacep.com.br/ws/${cleanCEP}/json/`
      );

      if (!response.ok) {
        throw new Error("Erro na busca do CEP.");
      }

      const data = await response.json();

      if (data.erro) {
        throw new Error("CEP não encontrado.");
      }

      // Encontrar o estado com base na sigla retornada
      const estadoEncontrado = estados.find(
        (estado) => estado.sigla === data.uf
      );

      if (!estadoEncontrado) {
        throw new Error("Estado não encontrado para o CEP informado.");
      }

      // Atualizar os campos de endereço com os dados retornados
      setFormData((prev) => ({
        ...prev,
        ENDERECO_RUA: data.logradouro || "",
        ENDERECO_BAIRRO: data.bairro || "",
        ENDERECO_CEP: data.cep || "",
        ESTADO_CODIGO_IBGE: estadoEncontrado.codigo_ibge || "",
      }));

      // Buscar os municípios para o estado encontrado
      const municipiosList = await fetchMunicipios(
        estadoEncontrado.codigo_ibge
      );

      // Encontrar o município com base no código IBGE retornado
      const municipioEncontrado = municipiosList.find(
        (m) => m.id.toString() === data.ibge
      );

      if (municipioEncontrado) {
        setFormData((prev) => ({
          ...prev,
          MUNICIPIO_CODIGO_IBGE: municipioEncontrado.id.toString(),
        }));
      } else {
        setCepError("Município não encontrado para o CEP informado.");
      }
    } catch (error) {
      setCepError(error.message);
    } finally {
      setCepLoading(false);
    }
  };

  const renderInformacoesGerais = () => (
    <div className="space-y-4">
      {/* Seção de Tipo de Pessoa */}
      <div className="flex items-center space-x-4 mb-4">
        <label className="flex items-center">
          <input
            type="radio"
            name="FISICA_JURIDICA"
            value="J"
            checked={formData.FISICA_JURIDICA === "J"}
            onChange={handleInputChange}
            className="mr-2"
          />
          Pessoa Jurídica
        </label>
        <label className="flex items-center">
          <input
            type="radio"
            name="FISICA_JURIDICA"
            value="F"
            checked={formData.FISICA_JURIDICA === "F"}
            onChange={handleInputChange}
            className="mr-2"
          />
          Pessoa Física
        </label>
      </div>

      {/* Seção de Tipo */}
      <div className="flex items-center space-x-4 mb-4">
        <label className="flex items-center">
          <input
            type="radio"
            name="TIPO"
            value="R"
            checked={formData.TIPO === "R"}
            onChange={handleInputChange}
            className="mr-2"
          />
          Revenda
        </label>
        <label className="flex items-center">
          <input
            type="radio"
            name="TIPO"
            value="F"
            checked={formData.TIPO === "F"}
            onChange={handleInputChange}
            className="mr-2"
          />
          Final
        </label>
      </div>

      {/* Seção de Informações Gerais */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Razão Social */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Razão Social*
          </label>
          <input
            type="text"
            name="RAZAO_SOCIAL"
            value={formData.RAZAO_SOCIAL}
            onChange={handleInputChange}
            className="w-full p-2 border rounded focus:ring-1 focus:ring-red-500"
            required
          />
        </div>

        {/* Nome Fantasia */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Nome Fantasia*
          </label>
          <input
            type="text"
            name="NOME_FANTASIA"
            value={formData.NOME_FANTASIA}
            onChange={handleInputChange}
            className="w-full p-2 border rounded focus:ring-1 focus:ring-red-500"
            required
          />
        </div>

        {/* CNPJ/CPF */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {formData.FISICA_JURIDICA === "J" ? "CNPJ*" : "CPF*"}
          </label>
          <InputMask
            mask={
              formData.FISICA_JURIDICA === "J"
                ? "99.999.999/9999-99"
                : "999.999.999-99"
            }
            name="CNPJ"
            value={formData.CNPJ}
            onChange={handleInputChange}
            onBlur={(e) => handleDocumentValidation(e.target.value)}
            className={`w-full p-2 border rounded focus:ring-1 focus:ring-red-500 ${
              documentError ? "border-red-500" : ""
            }`}
            required
          />
          {documentError && (
            <span className="text-sm text-red-500">{documentError}</span>
          )}
        </div>

        {/* Telefone Principal */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Telefone Principal*
          </label>
          <InputMask
            mask="(99) 99999-9999"
            name="TELEFONE_GERAL_1"
            value={formData.TELEFONE_GERAL_1}
            onChange={handleInputChange}
            className="w-full p-2 border rounded focus:ring-1 focus:ring-red-500"
            required
          />
        </div>

        {/* Telefone Secundário */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Telefone Secundário
          </label>
          <InputMask
            mask="(99) 99999-9999"
            name="TELEFONE_GERAL_2"
            value={formData.TELEFONE_GERAL_2}
            onChange={handleInputChange}
            className="w-full p-2 border rounded focus:ring-1 focus:ring-red-500"
          />
        </div>

        {/* Email */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Email*
          </label>
          <input
            type="email"
            name="EMAIL_NFE"
            value={formData.EMAIL_NFE}
            onChange={handleInputChange}
            className="w-full p-2 border rounded focus:ring-1 focus:ring-red-500"
            required
          />
        </div>

        {/* CEP */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            CEP*
          </label>
          <InputMask
            mask="99999-999"
            name="ENDERECO_CEP"
            value={formData.ENDERECO_CEP}
            onChange={handleInputChange}
            onBlur={(e) => fetchAddressByCEP(e.target.value)}
            className="w-full p-2 border rounded focus:ring-1 focus:ring-red-500"
            required
          />
          {cepLoading && (
            <span className="text-sm text-gray-500">Buscando endereço...</span>
          )}
          {cepError && <span className="text-sm text-red-500">{cepError}</span>}
        </div>

        {/* Endereço */}
        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Endereço*
          </label>
          <input
            type="text"
            name="ENDERECO_RUA"
            value={formData.ENDERECO_RUA}
            onChange={handleInputChange}
            className="w-full p-2 border rounded focus:ring-1 focus:ring-red-500"
            required
          />
        </div>

        {/* Número */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Número
          </label>
          <input
            type="text"
            name="ENDERECO_NUMERO"
            value={formData.ENDERECO_NUMERO}
            onChange={handleInputChange}
            className="w-full p-2 border rounded focus:ring-1 focus:ring-red-500"
          />
        </div>

        {/* Bairro */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Bairro*
          </label>
          <input
            type="text"
            name="ENDERECO_BAIRRO"
            value={formData.ENDERECO_BAIRRO}
            onChange={handleInputChange}
            className="w-full p-2 border rounded focus:ring-1 focus:ring-red-500"
            required
          />
        </div>

        {/* Estado */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Estado*
          </label>
          <select
            name="ESTADO_CODIGO_IBGE"
            value={formData.ESTADO_CODIGO_IBGE}
            onChange={handleInputChange}
            className="w-full p-2 border rounded focus:ring-1 focus:ring-red-500"
            required
          >
            <option value="">Selecione o Estado</option>
            {estados.map((estado) => (
              <option key={estado.codigo_ibge} value={estado.codigo_ibge}>
                {estado.nome}
              </option>
            ))}
          </select>
        </div>

        {/* Município */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Município*
          </label>
          {loadingMunicipios ? (
            <div className="w-full p-2 border rounded bg-gray-100">
              Carregando municípios...
            </div>
          ) : municipioError ? (
            <div className="w-full p-2 border rounded bg-red-100 text-red-700">
              {municipioError}
            </div>
          ) : (
            <select
              name="MUNICIPIO_CODIGO_IBGE"
              value={formData.MUNICIPIO_CODIGO_IBGE}
              onChange={handleInputChange}
              className="w-full p-2 border rounded focus:ring-1 focus:ring-red-500"
              required
              disabled={!formData.ESTADO_CODIGO_IBGE || municipios.length === 0}
            >
              <option value="">Selecione o Município</option>
              {municipios.map((municipio) => (
                <option key={municipio.id} value={municipio.id.toString()}>
                  {municipio.nome}
                </option>
              ))}
            </select>
          )}
        </div>

        {/* Inscrição Estadual */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Inscrição Estadual
          </label>
          <input
            type="text"
            name="INSCRICAO_ESTADUAL"
            value={formData.INSCRICAO_ESTADUAL}
            onChange={handleInputChange}
            className="w-full p-2 border rounded focus:ring-1 focus:ring-red-500"
          />
        </div>

        {/* Inscrição Municipal */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Inscrição Municipal
          </label>
          <input
            type="text"
            name="INSCRICAO_MUNICIPAL"
            value={formData.INSCRICAO_MUNICIPAL}
            onChange={handleInputChange}
            className="w-full p-2 border rounded focus:ring-1 focus:ring-red-500"
          />
        </div>

        {/* Inscrição Rural */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Inscrição Rural
          </label>
          <input
            type="text"
            name="INSCRICAO_RURAL"
            value={formData.INSCRICAO_RURAL}
            onChange={handleInputChange}
            className="w-full p-2 border rounded focus:ring-1 focus:ring-red-500"
          />
        </div>

        {/* Suframa */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Suframa
          </label>
          <input
            type="text"
            name="SUFRAMA"
            value={formData.SUFRAMA}
            onChange={handleInputChange}
            className="w-full p-2 border rounded focus:ring-1 focus:ring-red-500"
          />
        </div>

        {/* Regime Tributário */}
        <div>
          <label className="flex items-center space-x-2">
            <input
              type="checkbox"
              name="REGIME_TRIBUTARIO"
              checked={formData.REGIME_TRIBUTARIO}
              onChange={handleInputChange}
              className="rounded text-red-600 focus:ring-red-500"
            />
            <span className="text-sm font-medium text-gray-700">
              Regime Tributário Diferenciado
            </span>
          </label>
        </div>
      </div>
    </div>
  );

  const renderInformacoesComerciais = () => (
    <div className="space-y-6">
      <h3 className="text-lg font-medium text-gray-900">
        Principais Fornecedores
      </h3>
      {formData.fornecedores.map((fornecedor, index) => (
        <div key={index} className="border p-4 rounded-lg">
          <h4 className="text-sm font-medium text-gray-700 mb-2">
            Fornecedor {index + 1}
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Nome do Fornecedor */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Nome
              </label>
              <input
                type="text"
                name="nome"
                value={fornecedor.nome}
                onChange={(e) => handleInputChange(e, index)}
                className="w-full p-2 border rounded focus:ring-1 focus:ring-red-500"
              />
            </div>
            {/* Telefone do Fornecedor */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Telefone
              </label>
              <InputMask
                mask="(99) 99999-9999"
                name="telefone"
                value={fornecedor.telefone}
                onChange={(e) => handleInputChange(e, index)}
                className="w-full p-2 border rounded focus:ring-1 focus:ring-red-500"
              />
            </div>
          </div>
        </div>
      ))}
    </div>
  );

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-y-auto">
      {/* Tela de erro caso serverError esteja setado */}
      {serverError && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-60">
          <div className="bg-white p-6 rounded shadow-lg">
            <h2 className="text-xl font-bold mb-4">Erro</h2>
            <pre className="mb-4">{serverError}</pre>
            <button
              onClick={() => setServerError(null)}
              className="px-4 py-2 bg-red-600 text-white rounded"
            >
              Fechar
            </button>
          </div>
        </div>
      )}

      <div className="bg-white rounded-lg p-6 w-full max-w-3xl my-8">
        {/* Cabeçalho do Formulário */}
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold">Novo Cliente</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Mensagem de Erro */}
        {error && (
          <div className="mb-4 p-2 bg-red-100 border-l-4 border-red-500 text-red-700">
            {error}
          </div>
        )}

        {/* Abas de Navegação */}
        <div className="flex space-x-2 mb-4">
          <button
            onClick={() => setActiveTab("geral")}
            className={`hidden px-4 py-2 rounded-lg ${
              activeTab === "geral"
                ? "bg-red-600 text-white"
                : "bg-gray-100 text-gray-700 hover:bg-gray-200"
            }`}
          >
            Informações Gerais
          </button>
          <button
            onClick={() => setActiveTab("comercial")}
            className={`hidden px-4 py-2 rounded-lg ${
              activeTab === "comercial"
                ? "bg-red-600 text-white"
                : "bg-gray-100 text-gray-700 hover:bg-gray-200"
            }`}
          >
            Informações Comerciais
          </button>
        </div>

        {/* Formulário */}
        <form onSubmit={handleSubmit}>
          <div className="max-h-[60vh] overflow-y-auto px-1">
            {activeTab === "geral"
              ? renderInformacoesGerais()
              : renderInformacoesComerciais()}
          </div>

          {/* Botões de Ação */}
          <div className="mt-6 pt-4 border-t">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between">
              <div className="text-red-500 mb-2 md:mb-0">
                {formLoading === false && (apiMsg)}

                {formLoading && (
                        <div className="flex items-center ml-4">
                          <svg
                            className="animate-spin -ml-1 mr-2 h-5 w-5 text-red-600"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                          >
                            <circle
                              className="opacity-25"
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="currentColor"
                              strokeWidth="4"
                            ></circle>
                            <path
                              className="opacity-75"
                              fill="currentColor"
                              d="M4 12a8 8 0 018-8v8H4z"
                            ></path>
                          </svg>
                          <span className="text-sm text-gray-600">
                            Enviando cliente...
                          </span>
                        </div>
                )}
              </div>
              <div className="flex justify-end space-x-2">
                <button
                  type="button"
                  onClick={onClose}
                  className="px-4 py-2 border rounded hover:bg-gray-50"
                  disabled={formLoading}
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
                  disabled={formLoading}
                >
                  {formLoading ? "Salvando..." : "Salvar"}
                </button>
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CustomerForm;
